/**
 * The people in the room, and what a host can do about them.
 *
 * Lives beside the softphone rather than on the page that shows it, for the same reason the dock
 * does: it is driven entirely by the softphone context, and the dev-time state gallery
 * (`routes/dev-phone.tsx`) renders it over a hand-built one so every state of it — somebody
 * muted, somebody deaf, a switch refusing a command, a rep who is only a guest — can be looked
 * at without two handsets and a conference profile.
 */

import {
  Ear,
  EarOff,
  Mic,
  MicOff,
  MoreHorizontal,
  Pause,
  Send,
  UserMinus,
  X,
} from "lucide-react";
import * as React from "react";
import { Badge, StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Callout } from "@/components/ui/feedback";
import { Input } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { cn } from "@/lib/utils";
import { SHOW_TRANSFER_TO_NUMBER } from "@/softphone/call-control";
import {
  describeLeg,
  MEMBER_ACTION_LABELS,
  memberActionApplies,
  rowMuted,
  type ConferenceParticipant,
  type MemberAction,
} from "@/softphone/conference";
import { useSoftphone } from "@/softphone/softphone-context";

/**
 * What one row of the roster is: the potential client, a colleague, the rep's own line.
 *
 * Absent until core-api answers, and absent for good on a leg it could not place — the roster is
 * the same list either way, so the badge appears beside a name rather than in a column that
 * would sit empty. The two tones are the distinction a rep is actually scanning for: whether a
 * row is somebody outside the firm or somebody in it.
 */
function LegBadge({ participant }: { participant: ConferenceParticipant }) {
  const label = describeLeg(participant);
  if (!label) return null;
  const outside = participant.kind === "lead" || participant.kind === "caller";
  return (
    <Badge tone={outside ? "accent" : "outline"} size="sm">
      {label}
    </Badge>
  );
}

/**
 * Who is in the room, live.
 *
 * This list is the visible proof that the liveArray is wired: the switch only pushes it to a
 * client that both has the `livearray-sync` flag set on the conference profile and has
 * subscribed to the channel named in the join event (see `softphone/conference.ts`). Missing
 * either produces an empty list rather than an error, which is why an empty roster says so
 * explicitly instead of rendering nothing.
 *
 * It is also where a host acts on the people in it. Those controls appear only for a host,
 * because the switch only accepts them from one — and only *say* they are missing when the rep
 * might have expected them, which is the case of being in somebody else's room.
 */
export function ConferenceRoster({
  title, callId = null, labels = {}, held, memberTransfer = true,
}: {
  title?: string;
  /**
   * This phone's own leg on the call the room belongs to, which the two controls that do not go
   * through a moderator command need: the rep's own Mute is their phone's, and Hold goes to
   * `POST /phone/room/hold`, which is addressed by the leg. Without it both are dimmed — the
   * state gallery renders this roster over a room that belongs to no call.
   */
  callId?: string | null;
  /** Better names for rows the switch names badly, by channel uuid (see `CallRoster`). */
  labels?: Record<string, string>;
  /**
   * The members the server says are held in the room, by channel uuid (see `CallRoster`). A hold
   * done in the room never shows on the roster's own `onHold`, and a held member is offered no
   * mute or deaf (`memberActionApplies`).
   */
  held?: ReadonlySet<string>;
  /**
   * Whether a row's menu offers "Transfer…", which sends that member to a bare extension or
   * number with no check that anybody is there. Off on a direct call (ADR-032): the Transfer
   * tile's "Blind transfer" does the same through the server, only to a colleague who is
   * online and through their room route. Seen live: a caller sent this way to an offline rep
   * was dropped with `USER_NOT_REGISTERED`.
   */
  memberTransfer?: boolean;
} = {}) {
  const phone = useSoftphone();
  const conference = phone.conference;
  // Whichever row is mid-transfer, so the destination field replaces that row's controls rather
  // than opening a second one somewhere else on the page.
  const [transferring, setTransferring] = React.useState<string | null>(null);
  // A refusal the console caught before the switch was involved. The switch's own refusals
  // arrive later and separately, on `phone.conferenceError`.
  const [error, setError] = React.useState<string | null>(null);

  if (!conference) return null;
  const host = conference.modChannel !== null;
  // The name on screen, and in every control's name too: a menu button called "Manage Outbound
  // Call" beside "Maria Lopez" sends a screen reader to the wrong person.
  const nameOf = (p: ConferenceParticipant) => (p.channelId && labels[p.channelId]) || p.name;
  const isHeld = (p: ConferenceParticipant) =>
    p.onHold || (p.channelId !== null && held?.has(p.channelId) === true);
  // This phone's own mute, which the switch does not know about and the rep's row must (`rowMuted`).
  const phoneMuted = callId !== null && phone.calls.some((c) => c.id === callId && c.muted);
  const rows = conference.participants.map((p) => ({ ...p, muted: rowMuted(p, phoneMuted) }));

  const run = async (action: MemberAction, p: ConferenceParticipant, value?: string) => {
    setError(null);
    try {
      await phone.memberCommand(action, p, value);
      setTransferring(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Could not ${MEMBER_ACTION_LABELS[action]}.`);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        {/* A direct call's room is named after nothing a rep would recognise (ADR-032), so the
            caller of this roster names it instead. */}
        {title ? (
          <span className="text-xs font-medium text-text-muted">{title}</span>
        ) : (
          <span className="font-mono text-xs text-text-muted">{conference.name}</span>
        )}
        <Badge tone="neutral" size="sm">
          {conference.participants.length === 1
            ? "1 person"
            : `${conference.participants.length} people`}
          {conference.role === "moderator" && " · you host"}
        </Badge>
      </div>

      {conference.participants.length === 0 ? (
        <p className="text-xs text-text-muted">
          Connected to the room, but the switch has not sent a member list. Check that the
          conference profile carries the <span className="font-mono">livearray-sync</span> flag.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-md border border-border">
          {rows.map((p) => (
            <li key={p.hashKey} className="px-3 py-2">
              {/* The menu button is taller than the text beside it, and only some rows carry
                  one — the rep's own does not. Reserving its height keeps the list even. */}
              <div className="flex min-h-7 items-center gap-2.5">
                {/* Speaking is the one thing worth animating: on a call with no video it is the
                    only cue for who is talking over whom. */}
                <StatusDot state={p.talking ? true : null} />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {nameOf(p)}
                  {p.isSelf && <span className="ml-1.5 text-xs text-text-subtle">(you)</span>}
                </span>
                <LegBadge participant={p} />
                {p.floor && !p.muted && (
                  <Badge tone="neutral" size="sm">
                    Speaking
                  </Badge>
                )}
                <MemberControls
                  participant={p}
                  name={nameOf(p)}
                  held={isHeld(p)}
                  host={host}
                  onAct={(action) => void run(action, p)}
                  onSelfMute={
                    callId === null ? null : () => void phone.toggleMute(callId)
                  }
                  onTransfer={
                    SHOW_TRANSFER_TO_NUMBER && memberTransfer && host && !p.isSelf
                      ? () => {
                          setError(null);
                          setTransferring(p.hashKey);
                        }
                      : null
                  }
                />
              </div>

              {transferring === p.hashKey && (
                <TransferMemberForm
                  participant={p}
                  onCancel={() => setTransferring(null)}
                  onSend={(destination) => run("transfer", p, destination)}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {(error ?? phone.conferenceError) && (
        <Callout tone="danger">{error ?? phone.conferenceError}</Callout>
      )}

      {!host && conference.participants.length > 0 && (
        <p className="text-xs text-text-muted">
          You are a guest in this room, so the switch will not let you mute or remove anybody in
          it. Only the room's host can.
        </p>
      )}
    </div>
  );
}

/**
 * What a rep can do to one person in the room, as controls rather than as a menu.
 *
 * Mute and Deafen are toggles — the roster already says which way each is set, so the control
 * that shows the state is the control that changes it, `aria-pressed` and all. They sit in fixed
 * slots, in the same order on every row, and a slot that cannot act on this row is dimmed with
 * the reason in its tooltip rather than left out: a row whose controls are missing shifts the
 * ones beside it, and this list is scanned down a column, not read across.
 *
 * **Hold is not one of them, and deliberately.** The switch can hold one member — the conference
 * API's `hold` takes a member target — but nothing we run does: `POST /phone/room/hold` holds
 * every member who is not a rep, and on a call the ACD connected the ACD holds the caller. A
 * control on somebody's row that silently acts on everybody outside the firm claims a precision
 * the system does not have, so hold stays where it means what it says: the call's own Hold tile.
 * The row still *reports* it — a held member is marked, and cannot be muted or deafened until
 * they are back.
 *
 * The one-way actions stay behind the ⋯ — Transfer sends somebody out of the room for good and
 * Remove hangs them up, and neither is something to hit while reaching for Mute.
 *
 * Mute on the rep's own row is their phone's own mute, not a moderator command: it is the same
 * mute as the phone's Mute tile, and the row is where a rep looks when they want to know if the
 * room can hear them.
 */
function MemberControls({
  participant, name, held, host, onAct, onSelfMute, onTransfer,
}: {
  participant: ConferenceParticipant;
  name: string;
  held: boolean;
  host: boolean;
  onAct: (action: MemberAction) => void;
  /** Null when the roster does not know which leg is this phone's. */
  onSelfMute: (() => void) | null;
  /** Null hides the menu: a guest's row, the rep's own, or a room that sends people on some
   *  other way. */
  onTransfer: (() => void) | null;
}) {
  const mutable = memberActionApplies(participant.muted ? "unmute" : "mute", participant, held);
  const deafable = memberActionApplies(participant.deaf ? "undeaf" : "deaf", participant, held);
  const hostOnly = host ? null : "Only the room's host can do this";
  const notWhileHeld = held ? "They are on hold" : null;

  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {/* Held is reported, not set, and keeps a slot of its own either way: the controls beside
          it must start in the same place on every row. Hold itself is the call's Hold tile. */}
      <span className="flex size-7 items-center justify-center">
        {held && (
          <Tooltip content={`${participant.isSelf ? "You are" : `${name} is`} on hold`}>
            <Pause
              className="size-3.5 text-warning-11"
              aria-label={`${participant.isSelf ? "You are" : `${name} is`} on hold`}
            />
          </Tooltip>
        )}
      </span>
      <RowToggle
        label={
          participant.isSelf
            ? participant.muted
              ? "Unmute yourself"
              : "Mute yourself"
            : participant.muted
              ? `Unmute ${name}`
              : `Mute ${name}`
        }
        pressed={participant.muted}
        icon={participant.muted ? MicOff : Mic}
        reason={
          participant.isSelf
            ? onSelfMute === null
              ? "This roster does not know which leg is yours"
              : null
            : (hostOnly ?? notWhileHeld ?? (mutable ? null : "Not right now"))
        }
        onToggle={
          participant.isSelf
            ? (onSelfMute ?? undefined)
            : () => onAct(participant.muted ? "unmute" : "mute")
        }
      />
      <RowToggle
        label={
          participant.isSelf
            ? "Deafen"
            : participant.deaf
              ? `Let ${name} hear the room again`
              : `Stop ${name} hearing the room`
        }
        pressed={participant.deaf}
        icon={participant.deaf ? EarOff : Ear}
        reason={
          participant.isSelf
            ? "Deafen is for other people in the room"
            : (hostOnly ?? notWhileHeld ?? (deafable ? null : "Not right now"))
        }
        onToggle={() => onAct(participant.deaf ? "undeaf" : "deaf")}
      />
      {host && !participant.isSelf ? (
        <MemberMenu participant={participant} name={name} onAct={onAct} onTransfer={onTransfer} />
      ) : (
        // The slot is kept so every row's controls end in the same place.
        <span className="size-7" aria-hidden />
      )}
    </span>
  );
}

/**
 * One slot in a row's controls: an icon that says how a flag is set and sets it.
 *
 * A reason makes it inert rather than hiding it, and the tooltip carries the reason in place of
 * the label. Disabled buttons take no pointer events, so the tooltip hangs off a wrapper — a
 * dimmed control whose reason cannot be read is worse than no control at all.
 */
function RowToggle({
  label, reason = null, pressed, icon: Icon, onToggle,
}: {
  label: string;
  reason?: string | null;
  pressed: boolean;
  icon: typeof Mic;
  onToggle?: () => void;
}) {
  const inert = reason !== null || onToggle === undefined;
  return (
    <Tooltip content={reason ?? label}>
      <span className="inline-flex">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={label}
          aria-pressed={inert ? undefined : pressed}
          aria-disabled={inert || undefined}
          onClick={inert ? undefined : onToggle}
          className={cn(
            "size-7",
            inert && "cursor-default opacity-40 hover:bg-transparent",
            !inert && pressed && "bg-accent-3 text-accent-11 hover:bg-accent-4",
          )}
        >
          <Icon className="size-3.5" />
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * The two things that are not toggles: sending somebody out of the room, and hanging them up.
 *
 * They stay behind a menu now that Mute, Deafen and Hold are controls of their own. Both are
 * one-way — nothing brings a transferred member back, and Remove ends their call — so neither
 * belongs in the row of icons a rep reaches into without looking.
 */
function MemberMenu({
  participant, name, onAct, onTransfer,
}: {
  participant: ConferenceParticipant;
  name: string;
  onAct: (action: MemberAction) => void;
  /** Null leaves Transfer out: this room sends people on some other way. */
  onTransfer: (() => void) | null;
}) {
  // Set when "Transfer…" is what closed the menu. Every other item leaves the row as it was, so
  // focus belongs back on the button that opened it; that one replaces the row with a field that
  // is waiting to be typed into, and Radix would otherwise pull the caret back out of it.
  const openingTransfer = React.useRef(false);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon-sm" className="size-7" aria-label={`More for ${name}`}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        onCloseAutoFocus={(event) => {
          if (!openingTransfer.current) return;
          openingTransfer.current = false;
          event.preventDefault();
        }}
      >
        {onTransfer && (
          <DropdownMenuItem
            onSelect={() => {
              openingTransfer.current = true;
              onTransfer();
            }}
          >
            <Send />
            {MEMBER_ACTION_LABELS.transfer}…
          </DropdownMenuItem>
        )}
        {memberActionApplies("remove", participant) && (
          <DropdownMenuItem destructive onSelect={() => onAct("remove")}>
            <UserMinus />
            {MEMBER_ACTION_LABELS.remove}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Where somebody in the room is being sent.
 *
 * Sending them takes them out of the room and puts them wherever this leads, which is why it
 * says so: the rest of the room carries on without them, and nothing brings them back. The
 * destination is dialled in their own leg's context — the switch keeps the dialplan the channel
 * already had — so an extension is enough and there is nothing here to configure.
 */
function TransferMemberForm({
  participant,
  onSend,
  onCancel,
}: {
  participant: ConferenceParticipant;
  onSend: (destination: string) => Promise<void>;
  onCancel: () => void;
}) {
  const id = React.useId();
  const [destination, setDestination] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  // `autoFocus` loses this race. The menu that opened this form returns focus to the button it
  // hung off as it closes, which happens after this input has mounted — so a rep who picked
  // "Transfer…" would find the caret back on the ⋯ button and their first keystrokes nowhere.
  // A frame later the menu has finished and the field is the only thing anyone wants focused.
  const field = React.useRef<HTMLInputElement>(null);
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => field.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, []);

  return (
    <form
      className="mt-2 flex flex-col gap-1.5 border-t border-border pt-2"
      onSubmit={(e) => {
        e.preventDefault();
        const typed = destination.trim();
        if (!typed || busy) return;
        setBusy(true);
        void onSend(typed).finally(() => setBusy(false));
      }}
    >
      <label htmlFor={id} className="text-xs text-text-muted">
        Send {participant.name} out of the room to
      </label>
      <div className="flex gap-1.5">
        <Input
          id={id}
          ref={field}
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder="Extension or number"
          inputMode="tel"
          autoComplete="off"
          className="font-mono"
        />
        <Button type="submit" loading={busy} disabled={!destination.trim()}>
          <Send />
          Send
        </Button>
        <Button type="button" variant="ghost" size="icon" aria-label="Cancel" onClick={onCancel}>
          <X />
        </Button>
      </div>
    </form>
  );
}
